// Engineer PWA read-only document viewer (Session AFD). Opened from a Fault Assistant source card so
// the engineer can read the retrieved passage in its document context while standing at the lift.
//
// READ ONLY, in every respect. There is no write, edit, delete, tag, confirm or retry control here and
// none may ever be added: the engineer shell has no document-management role, and the only office
// mutation routes for a knowledge document are office/admin guarded anyway. The single interactive
// control is Close.
//
// SELF-CONTAINED OVERLAY, deliberately NOT src/components/ModalShell.tsx. ModalShell hard-codes the
// "office-ui" class on its overlay, which switches on the office density system (13.5px base) and
// overrides brand colour variables. It is an office component. The engineer PWA has no modal of its
// own, so this builds the small amount it needs: fixed full-screen panel, its own Escape handler,
// focus moved onto the panel on open and restored on close, and the body scroll locked while open.
//
// The tab order is confined by the CALLER, not by a focus trap built here: the Fault Assistant marks
// its page content `inert` while this viewer is open and renders the viewer outside that container,
// the pattern OfficeLayout already uses for its navigation drawer. That is what makes the
// aria-modal="true" below true, rather than merely asserted.
import { useEffect, useRef } from "react";
import { docTypeLabel, type KnowledgeQuerySource } from "../../lib/knowledgeTypes";

export function EngineerDocumentViewer({
  source,
  onClose,
}: {
  source: KnowledgeQuerySource;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // onClose held in a ref, so the open effect below can depend on NOTHING and therefore runs exactly
  // once per open. It used to depend on [onClose], and the Fault Assistant passes a fresh inline
  // arrow every render; the parent re-renders whenever its engineer data context changes, so the
  // effect tore down and re-ran mid-read, pulling focus back to the panel and scrolling the reader
  // back to the top. The ref keeps that true whatever any future caller passes, rather than relying
  // on every caller remembering to memoise its handler.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const equipment = [source.manufacturer, source.liftModel].filter(Boolean).join(" · ");

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[var(--ele-shell)]">
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Document: ${source.fileName}`}
        className="min-h-dvh p-4 outline-none"
      >
        <div className="flex items-start gap-3 border-b border-ele-purple-accent pb-3">
          <div className="min-w-0 flex-1">
            <h2 className="break-words font-heading text-lg tracking-wide text-ele-strong">{source.fileName}</h2>
            <p className="mt-0.5 font-menu text-[11px] uppercase tracking-wide text-ele-body">
              {docTypeLabel(source.docType)}
              {equipment ? ` · ${equipment}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close document"
            className="flex min-h-11 min-w-11 flex-none items-center justify-center rounded-ele border border-ele-blue px-3 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
          >
            Close
          </button>
        </div>

        <section className="mt-4">
          <h3 className="mb-1 font-menu text-xs uppercase tracking-wider text-ele-body">Document summary</h3>
          <p className="text-sm text-ele-body">
            {source.documentSummary ?? "No summary is on file for this document."}
          </p>
        </section>

        <section className="mt-5">
          <h3 className="mb-1 font-menu text-xs uppercase tracking-wider text-ele-body">Relevant passage</h3>
          {/* The retrieved passage is highlighted with the locked amber tint and dark body text, so it
              is unmistakably the part the assistant read and stays readable on a bright screen. */}
          <div className="rounded-ele border border-[#b8860b] bg-[#fbf3dd] p-3">
            <p className="whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-ele-bg">
              {source.chunkText}
            </p>
          </div>
        </section>

        <section className="mt-5">
          <h3 className="mb-1 font-menu text-xs uppercase tracking-wider text-ele-body">Page</h3>
          <p className="text-sm text-ele-body">
            {source.pageNumber != null
              ? `Found on approximately page ${source.pageNumber}.`
              : "Page number cannot be confirmed for this document."}
          </p>
        </section>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 min-h-11 w-full rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          Back to the answer
        </button>
      </div>
    </div>
  );
}
